import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Heart, Play, RefreshCw, Activity, Shield, Building2, CalendarCheck,
  CheckCircle2, AlertTriangle, XCircle, Clock, Bell, ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GROUP_META = {
  smoke_tests:         { icon: Activity, label: 'Smoke Tests', weight: '30%' },
  zatca_health:        { icon: Shield,   label: 'ZATCA Health', weight: '30%' },
  company_profile:     { icon: Building2,label: 'Company Profile', weight: '15%' },
  month_end_readiness: { icon: CalendarCheck, label: 'Month-End Readiness', weight: '25%' },
};

const STATUS_STYLE = {
  HEALTHY:  { bg: 'from-emerald-50 to-emerald-100 border-emerald-300', text: 'text-emerald-900', subtext: 'text-emerald-700', icon: CheckCircle2, label: 'HEALTHY', dot: 'bg-emerald-500' },
  WARNING:  { bg: 'from-amber-50 to-amber-100 border-amber-300', text: 'text-amber-900', subtext: 'text-amber-700', icon: AlertTriangle, label: 'WARNING', dot: 'bg-amber-500' },
  CRITICAL: { bg: 'from-rose-50 to-rose-100 border-rose-300', text: 'text-rose-900', subtext: 'text-rose-700', icon: XCircle, label: 'CRITICAL', dot: 'bg-rose-500' },
  PASS:     { dot: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-800' },
};

const CHECK_CHIP = {
  PASS:     'bg-emerald-100 text-emerald-800 border-emerald-200',
  WARNING:  'bg-amber-100 text-amber-800 border-amber-200',
  CRITICAL: 'bg-rose-100 text-rose-800 border-rose-200',
};

const HealthMonitor = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [nextRun, setNextRun] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [r, n, h] = await Promise.all([
        axiosInstance.get('/health-monitor/last-report'),
        axiosInstance.get('/health-monitor/next-run'),
        axiosInstance.get('/health-monitor/history?limit=14'),
      ]);
      if (r.data?.id) setReport(r.data);
      setNextRun(n.data);
      setHistory(h.data?.runs || []);
    } catch (err) {
      // initial load can fail silently if no report yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => { await loadAll(); };
    init();
  }, [loadAll]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axiosInstance.post('/health-monitor/run');
      setReport(r.data);
      const status = r.data.overall_status;
      if (status === 'HEALTHY') toast.success(`Healthy — ${r.data.overall_score}/100`);
      else if (status === 'WARNING') toast.warning(`Warning — ${r.data.alerts_count} alert(s)`);
      else toast.error(`Critical — ${r.data.alerts_count} alert(s)`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  // Feb 2026 · One-click heal for the ``recovery_readiness`` alert.
  // Forces a fresh backup + restore-test then re-runs the daily report.
  // Fixes the score in-place so the operator doesn't have to wait for
  // the 02:00 Riyadh scheduler.
  const [healing, setHealing] = useState(false);
  const healRecovery = async () => {
    setHealing(true);
    try {
      const r = await axiosInstance.post('/health-monitor/recovery-heal');
      const backupTriggered = r?.data?.heal?.backup_triggered;
      const restoreTriggered = r?.data?.heal?.restore_triggered;
      const parts = [];
      if (backupTriggered) parts.push('backup ✓');
      if (restoreTriggered) parts.push('restore-test ✓');
      toast.success(
        `Recovery healed — ${parts.join(' · ') || 'already fresh'} — score ${r?.data?.score || '?'}/100`,
        { duration: 6000 },
      );
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Heal failed');
    } finally {
      setHealing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const style = report ? STATUS_STYLE[report.overall_status] : null;
  const StatusIcon = style?.icon;

  return (
    <div className="space-y-5 max-w-6xl mx-auto" data-testid="health-monitor-page">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/settings')}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-4 h-4" /> Settings
        </button>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="run-now-btn"
          className={`px-5 py-2.5 rounded-lg font-bold text-sm text-white inline-flex items-center gap-2 ${
            running
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
          }`}
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Running…' : 'Run Now'}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Heart className="w-7 h-7 text-rose-500" />
            Daily ERP Health Monitor
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Unified monitoring layer. 4 check groups · weighted score · auto-runs daily at 06:00 Asia/Riyadh.
          </p>
        </div>
        {nextRun && (
          <div className="text-right bg-white border border-gray-200 rounded-lg px-3 py-2">
            <div className="text-xs text-gray-500">Next scheduled run</div>
            <div className="text-sm font-mono text-gray-900">
              <Clock className="w-3.5 h-3.5 inline mr-1" />
              {new Date(nextRun.next_run_utc).toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {report ? (
        <>
          {/* Overall banner */}
          <div className={`bg-gradient-to-r ${style.bg} border-2 rounded-xl p-6`} data-testid="overall-banner">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-full bg-white/60`}>
                  <StatusIcon className={`w-10 h-10 ${style.text}`} />
                </div>
                <div>
                  <h2 className={`text-3xl font-bold ${style.text}`} data-testid="overall-status">
                    {style.label}
                  </h2>
                  <p className={`text-sm ${style.subtext} mt-1`}>
                    {report.alerts_count > 0
                      ? `${report.alerts_count} alert(s) require attention`
                      : 'All systems operational'}
                    {' · '}Last checked {new Date(report.executed_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-6xl font-bold ${style.text}`} data-testid="overall-score">
                  {report.overall_score}
                  <span className="text-2xl text-gray-400">/100</span>
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Weighted Score · {report.duration_ms}ms
                </div>
              </div>
            </div>
          </div>

          {/* Alerts section */}
          {report.alerts?.length > 0 && (
            <div className="bg-white border-2 border-amber-200 rounded-xl overflow-hidden" data-testid="alerts-section">
              <div className="bg-amber-50 px-4 py-3 border-b border-amber-200 flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-700" />
                <h3 className="font-bold text-amber-900 text-sm">Active Alerts ({report.alerts.length})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {report.alerts.map((a, i) => {
                  const isRecovery = a.check === 'recovery_readiness';
                  return (
                    <div key={i} className="px-4 py-3 flex items-center gap-3" data-testid={`alert-${i}`}>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${CHECK_CHIP[a.severity.toUpperCase()]}`}>
                        {a.severity.toUpperCase()}
                      </span>
                      <span className="text-xs font-mono text-gray-500">{a.group}.{a.check}</span>
                      <span className="text-sm text-gray-800 flex-1">{a.message}</span>
                      {isRecovery && (
                        <button
                          onClick={healRecovery}
                          disabled={healing}
                          data-testid="heal-recovery-btn"
                          title="Run a fresh backup + restore-test now"
                          className={`text-[11px] font-bold px-3 py-1 rounded-full inline-flex items-center gap-1 ${
                            healing
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          }`}
                        >
                          {healing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                          {healing ? 'Fixing…' : 'Fix Now'}
                        </button>
                      )}
                      <span className="text-xs font-mono text-gray-400">{a.score}/100</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Groups grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="groups-grid">
            {report.groups.map((g) => {
              const meta = GROUP_META[g.group] || { icon: CheckCircle2, label: g.group, weight: '' };
              const Icon = meta.icon;
              const gstyle = STATUS_STYLE[g.summary.status] || STATUS_STYLE.HEALTHY;
              return (
                <div key={g.group} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`group-${g.group}`}>
                  <div className="px-4 py-3 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-indigo-50">
                        <Icon className="w-4 h-4 text-indigo-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{meta.label}</h3>
                        <p className="text-[10px] text-gray-500">Weight {meta.weight} · {g.duration_ms}ms</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${CHECK_CHIP[g.summary.status]}`}>
                        {g.score}/100
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-1.5 text-xs">
                    {g.checks?.map((c) => (
                      <div key={c.name} className="flex items-center gap-2">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                          c.status === 'PASS' ? 'bg-emerald-500'
                            : c.status === 'WARNING' ? 'bg-amber-500'
                            : 'bg-rose-500'
                        }`} />
                        <span className="font-mono text-gray-700 flex-1">{c.name}</span>
                        <span className={`text-[10px] font-semibold ${
                          c.status === 'PASS' ? 'text-emerald-700'
                            : c.status === 'WARNING' ? 'text-amber-700'
                            : 'text-rose-700'
                        }`}>{c.status} · {c.score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center text-gray-500" data-testid="no-report">
          <Heart className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="font-medium">No health report yet.</p>
          <p className="text-sm mt-1">Click &ldquo;Run Now&rdquo; or wait for the next scheduled run at 06:00 Riyadh.</p>
        </div>
      )}

      {/* Historical trend */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="history-section">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">14-Day Trend</h3>
          </div>
          <div className="px-4 py-3 flex items-end gap-1 h-32">
            {history.slice().reverse().map((h) => {
              const colour =
                h.overall_status === 'HEALTHY' ? 'bg-emerald-400' :
                h.overall_status === 'WARNING' ? 'bg-amber-400' : 'bg-rose-400';
              const pct = Math.max(8, h.overall_score || 0);
              return (
                <div key={h.id} className="flex-1 flex flex-col items-center gap-1" title={`${h.overall_score}/100 · ${new Date(h.executed_at).toLocaleDateString()}`}>
                  <div className="w-full flex-1 flex items-end">
                    <div className={`w-full ${colour} rounded-t`} style={{ height: `${pct}%` }} />
                  </div>
                  <span className="text-[9px] text-gray-500 font-mono">
                    {new Date(h.executed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthMonitor;
